import { useState } from "react";
import { apiService } from "../../services/api.service";
import type { LatencyProbeResponse } from "../../services/api.service";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Gauge, Play } from "lucide-react";

// Operations must match the backend _REDIS_OPERATIONS enum in latency_probe.py.
// Each is one atomic step.
const REDIS_OPERATIONS: { value: string; label: string }[] = [
  { value: "set_big", label: "SET big (save history blob — ×2-4/turn)" },
  { value: "get_big", label: "GET big (restore history — ×1/turn)" },
  {
    value: "checkpoint_patch",
    label: "GET+SET checkpoint (small — ×1/turn)",
  },
  {
    value: "hset",
    label: "HSET last_persisted_turn_id (barrier pair 1/2 · multi-instance)",
  },
  {
    value: "publish",
    label: "PUBLISH turn_ready (barrier pair 2/2 · multi-instance)",
  },
  { value: "ping", label: "PING (raw RTT baseline)" },
];

// Only the big-blob operations use payload_size_bytes; the rest are fixed-size.
const BIG_PAYLOAD_OPERATIONS = new Set(["set_big", "get_big"]);

// Turn-state scenarios sized from real-session logs ([TURN_STATE_SIZE] +
// [IMAGE_SIZE]). Each sets the history-blob payload for set_big / get_big.
// A base64-inlined scratchpad image measured ~132KB; text-only history 1–7KB.
const IMAGE_B64_BYTES = 132 * 1024; // one real scratchpad image, base64-inflated
const HISTORY_TEXT_BYTES = 7 * 1024; // a mid-length text-only turn
const MAX_IMAGE_B64_BYTES = Math.round(10 * 1024 * 1024 * 1.33); // 10MB cap × base64

interface Scenario {
  value: string;
  label: string;
  bytes: number; // -1 = custom (user-entered)
  note: string;
}

const SCENARIOS: Scenario[] = [
  {
    value: "empty",
    label: "Fresh turn (empty history)",
    bytes: 400,
    note: "First turn on a slide — just the seeded message. ~400 B.",
  },
  {
    value: "history",
    label: "Turn with history",
    bytes: HISTORY_TEXT_BYTES,
    note: "Mid-length text-only conversation, no images. ~7 KB.",
  },
  {
    value: "one_photo",
    label: "Turn with 1 photo",
    bytes: HISTORY_TEXT_BYTES + IMAGE_B64_BYTES,
    note: "History + one scratchpad image inlined (base64). ~140 KB.",
  },
  {
    value: "five_photos",
    label: "Turn with 5 photos",
    bytes: HISTORY_TEXT_BYTES + 5 * IMAGE_B64_BYTES,
    note: "History + five scratchpad-sized images. ~670 KB.",
  },
  {
    value: "mythical",
    label: "Mythical worst case (5 × 10 MB max)",
    bytes: HISTORY_TEXT_BYTES + 5 * MAX_IMAGE_B64_BYTES,
    note: "5 uploaded images at the 10 MB cap, base64. ~66 MB — the ceiling.",
  },
  {
    value: "custom",
    label: "Custom payload",
    bytes: -1,
    note: "Enter any size manually.",
  },
];

const SCENARIO_BY_VALUE: Record<string, Scenario> = Object.fromEntries(
  SCENARIOS.map((s) => [s.value, s]),
);

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatMs = (ms: number): string =>
  ms < 1 ? `${ms.toFixed(2)}ms` : `${ms.toFixed(1)}ms`;

const STAT_TILES: { key: keyof LatencyProbeResponse["latency"]; label: string }[] =
  [
    { key: "p50_ms", label: "p50" },
    { key: "p95_ms", label: "p95" },
    { key: "p99_ms", label: "p99" },
    { key: "min_ms", label: "min" },
    { key: "max_ms", label: "max" },
    { key: "mean_ms", label: "mean" },
  ];

export const ServiceLatencyPanel = () => {
  const [operation, setOperation] = useState<string>("set_big");
  const [iterations, setIterations] = useState(100);
  const [concurrency, setConcurrency] = useState(1);
  // Scenario drives payload size; "one_photo" (~140 KB) is the realistic peak.
  const [scenario, setScenario] = useState<string>("one_photo");
  // Only used when scenario === "custom".
  const [customBytes, setCustomBytes] = useState(143360);

  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState<LatencyProbeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const showPayloadField = BIG_PAYLOAD_OPERATIONS.has(operation);
  const activeScenario = SCENARIO_BY_VALUE[scenario];
  const isCustom = scenario === "custom";
  const effectivePayloadBytes = isCustom
    ? customBytes
    : (activeScenario?.bytes ?? 0);

  const handleRun = async () => {
    setError(null);
    setIsRunning(true);
    try {
      const response = await apiService.runLatencyProbe({
        operation,
        iterations,
        concurrency,
        payload_size_bytes: effectivePayloadBytes,
      });
      setResult(response);
    } catch (e) {
      console.error("Latency probe failed:", e);
      setError(e instanceof Error ? e.message : "Failed to run latency probe");
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-4">
      <Card>
        <CardContent className="flex flex-col gap-4 p-4">
          <div className="flex items-center gap-2">
            <Gauge className="size-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold text-foreground">
              Redis / ElastiCache Latency Probe
            </h3>
          </div>
          <p className="text-xs text-muted-foreground">
            Measures a single atomic Redis operation from inside the tutor
            backend's ECS task, reusing the production connection pool.
          </p>

          <div className="grid grid-cols-2 gap-4 max-md:grid-cols-1">
            <div className="col-span-2 flex flex-col gap-1.5 max-md:col-span-1">
              <Label htmlFor="probe-operation">Operation</Label>
              <Select value={operation} onValueChange={setOperation}>
                <SelectTrigger id="probe-operation">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REDIS_OPERATIONS.map((op) => (
                    <SelectItem key={op.value} value={op.value}>
                      {op.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="probe-iterations">Iterations</Label>
              <Input
                id="probe-iterations"
                type="number"
                min={1}
                max={5000}
                value={iterations}
                onChange={(e) => setIterations(Number(e.target.value))}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="probe-concurrency">Concurrency</Label>
              <Input
                id="probe-concurrency"
                type="number"
                min={1}
                max={100}
                value={concurrency}
                onChange={(e) => setConcurrency(Number(e.target.value))}
              />
            </div>

            {showPayloadField && (
              <div className="col-span-2 flex flex-col gap-1.5 max-md:col-span-1">
                <Label htmlFor="probe-scenario">Turn scenario</Label>
                <Select value={scenario} onValueChange={setScenario}>
                  <SelectTrigger id="probe-scenario">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SCENARIOS.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                        {s.bytes >= 0 && ` — ${formatBytes(s.bytes)}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {activeScenario && (
                  <p className="text-xs text-muted-foreground">
                    {activeScenario.note}
                    {!isCustom && (
                      <span className="ml-1 font-medium text-foreground">
                        Payload: {formatBytes(effectivePayloadBytes)}.
                      </span>
                    )}
                  </p>
                )}
              </div>
            )}

            {showPayloadField && isCustom && (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="probe-payload">Custom size (bytes)</Label>
                <Input
                  id="probe-payload"
                  type="number"
                  min={0}
                  max={80000000}
                  value={customBytes}
                  onChange={(e) => setCustomBytes(Number(e.target.value))}
                />
              </div>
            )}
          </div>

          <Button
            onClick={handleRun}
            disabled={isRunning}
            className="gap-2 self-start"
          >
            <Play className="size-4" />
            {isRunning ? "Running…" : "Run Probe"}
          </Button>

          {error && (
            <div className="rounded-md border border-destructive bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}
        </CardContent>
      </Card>

      {result && (
        <Card>
          <CardContent className="flex flex-col gap-3 p-4">
            <div className="flex items-center gap-2 text-sm">
              <span className="font-semibold text-foreground">
                {result.operation}
              </span>
              <span className="text-muted-foreground">
                {result.iterations} iters · concurrency {result.concurrency} ·{" "}
                {formatBytes(result.payload_size_bytes)}
              </span>
              <span className="ml-auto text-xs text-muted-foreground">
                {result.stage} · n={result.latency.count}
                {result.latency.errors > 0 && (
                  <span className="ml-1 text-destructive">
                    · {result.latency.errors} errors
                  </span>
                )}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2 max-md:grid-cols-2">
              {STAT_TILES.map((tile) => (
                <div
                  key={tile.key}
                  className="flex flex-col rounded-md border border-border/60 bg-muted/40 p-2 text-center"
                >
                  <span className="text-[0.65rem] uppercase tracking-wider text-muted-foreground">
                    {tile.label}
                  </span>
                  <span className="text-sm font-semibold text-foreground">
                    {formatMs(result.latency[tile.key])}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
